import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { ItemContent, ITEM_POSITIONS } from 'shared/Components/ItemContent'
import { ItemContentHandlerRenderer } from 'LiveBoard/components/shared/ItemContentHandlerRenderer'
import { Header } from './Header'
import CallToActionContainer from 'LiveBoard/containers/CallToActionContainer'
import { CTA_AREAS } from 'LiveBoard/components/shared/CallToAction/constants'
import { NavigationPanel } from './NavigationPanel'
import { getItemTypeConfig } from 'LiveBoard/components/shared/ItemType'
import { setIsGatingFormOpened } from 'LiveBoard/modules/itemViewer'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'

import './style.css.scss'

const NEXT_ITEM_ANIMATION_CLASS = "next-item-peek"
const NEXT_ITEM_ANIMATION_TIMEOUT = 7000
const NEXT_ITEM_ANIMATION_REMOVE_TIMEOUT = 3000

class ItemViewer extends React.Component {
  _isMounted = false

  constructor(props) {
    super(props)

    this.state = {
      animateNextItem: false
    }

    this.nextItemAnimationTimeout = null
    this.removeNextAnimationTimeout = null
  }

  componentDidMount() {
    this._isMounted = true

    this.setNextItemPeekTimeout()

    if (this.props.isGated != this.props.isGatingFormOpened) {
      this.props.setIsGatingFormOpened(this.props.isGated)
    }
  }

  componentDidUpdate(prevProps) {
    if(this.props.items.currentItemIndex != prevProps.items.currentItemIndex && this._isMounted) {
      this.setNextItemPeekTimeout()
    }

    if(this.props.isGated != prevProps.isGated && this.props.isGated != this.props.isGatingFormOpened) {
      this.props.setIsGatingFormOpened(this.props.isGated)
    }
  }

  componentWillUnmount() {
    this._isMounted = false
    this.clearAllTimeouts()
  }

  getPositionFor = (itemIndex) => {
    const {
      items: { prevItemIndex, currentItemIndex, nextItemIndex }
    } = this.props

    switch (itemIndex) {
      case prevItemIndex:
        return ITEM_POSITIONS.previous
      case nextItemIndex:
        return ITEM_POSITIONS.next
      case currentItemIndex:
        return ITEM_POSITIONS.current
    }
  }

  setNextItemPeekTimeout = () => {
      if (this._isMounted) {
        new Promise((resolve) => {
          this.nextItemAnimationTimeout = setTimeout(
            this.animateNextItem,
            NEXT_ITEM_ANIMATION_TIMEOUT
          )
          resolve()
        })
      }
  }

  clearNextItemPeekTimeout = () => {
    !!this.nextItemAnimationTimeout &&
      clearTimeout(this.nextItemAnimationTimeout)
  }

  animateNextItem = () => {
    this.setState({animateNextItem: true})
    if (this._isMounted) {
      this.removeNextAnimationTimeout = setTimeout(
        this.removeNextItemAnimation,
        NEXT_ITEM_ANIMATION_REMOVE_TIMEOUT
      )
    }
  }

  removeNextItemAnimation = () => {
    this.setState({ animateNextItem: false })
  }

  clearAllTimeouts = () => {
    !!this.removeNextAnimationTimeout &&
      clearTimeout(this.removeNextAnimationTimeout)
    this.clearNextItemPeekTimeout()
  }

  handleCancelGating = () => {
    if (this.props.gatingConfig.is_mandatory) {
      this.clearAllTimeouts()
      this.props.onClose()
    } else {
      this.props.setIsGatingFormOpened(false)
    }
  }

  handleNextItem = () => {
    this.clearAllTimeouts()
    this.props.onNextItem()
  }

  handlePrevItem = () => {
    this.clearAllTimeouts()
    this.props.onPrevItem()
  }

  isVideoItem = (item) => {
    const itemTypeConfig = getItemTypeConfig(item)
    return itemTypeConfig.isVideo || itemTypeConfig.isLinkAsVideo
  }

  ItemViewerWrapper = ({children}) => {
    return (
      <section
        className="item-viewer"
        ref={ref => this.itemViewer = ref}>
        {children}
      </section>
    )
  }

  getItem = (item, url) => {
    return !isEmpty(item) ? {...item, itemUrl: url} : item
  }

  render() {
    const { currentItem, themeColorHex, nextItemUrl, prevItemUrl } = this.props
    const { data, prevItemIndex, nextItemIndex } = this.props.items
    const ItemViewerWrapper = this.ItemViewerWrapper

    return isEmpty(currentItem) ? (
      <ItemViewerWrapper />
    ) : (
      <ItemViewerWrapper>
        <Header
          item={currentItem}
          onLike={this.props.onLike}
          onDownload={this.props.onDownload}
          onOpenInNewTab={this.props.onOpenInNewTab}
          onClose={this.props.onClose}
          isVideoItem={this.isVideoItem(currentItem)}
          blockDownload={this.props.blockDownload}
          isLiveEventActive={this.props.isLiveEventActive}
          isClassicTheme={this.props.isClassicTheme}
          themeColorHex={themeColorHex}
          showLikes={this.props.showLikes} />
        {
          map(data, (item) => {
            return (
              <ItemContentHandlerRenderer key={item.journey_index}>
                <ItemContent
                  key={item.journey_index}
                  item={item}
                  themeColorHex={themeColorHex}
                  itemPosition={this.getPositionFor(item.journey_index)}
                  requireSsl={this.props.requireSsl}
                  contentItemId={item.content_item_id}
                  onClick={this.props.onClickItem}
                  isVideoItem={this.isVideoItem(currentItem)} />
              </ItemContentHandlerRenderer>
            )
          })
        }
        { this.props.isGated && (
          <div>
            { this.props.isGatingFormOpened &&
              <div className="gating-overlay"></div>
            }
            <CallToActionContainer
              config={this.props.gatingConfig}
              area={CTA_AREAS.gating}
              themeColorHex={themeColorHex}
              autoOpenOnRender={true}
              onCancel={this.handleCancelGating}
              itemId={currentItem.id}
              modalClass="item-viewer-cta" />
          </div>
        )}
        { this.props.hasNavigation &&
          this.props.itemsCount > 1 && (
          <NavigationPanel
            className={this.state.animateNextItem ? NEXT_ITEM_ANIMATION_CLASS : ""}
            onNextItemHover={this.clearNextItemPeekTimeout}
            onNextItem={this.handleNextItem}
            onPrevItem={this.handlePrevItem}
            nextItem={this.getItem(data[nextItemIndex], nextItemUrl)}
            prevItem={this.getItem(data[prevItemIndex], prevItemUrl)} />
        )}
      </ItemViewerWrapper>
    )
  }
}

ItemViewer.propTypes = {
  items: PropTypes.object.isRequired,
  currentItem: PropTypes.object,
  gatingConfig: PropTypes.object.isRequired,
  isGated: PropTypes.bool.isRequired,
  themeColorHex: PropTypes.string.isRequired,
  onLike: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onOpenInNewTab: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onNextItem: PropTypes.func,
  onPrevItem: PropTypes.func,
  requireSsl: PropTypes.bool,
  onClickItem: PropTypes.func,
  blockDownload: PropTypes.bool,
  isLiveEventActive: PropTypes.bool,
  hasNavigation: PropTypes.bool.isRequired,
  isClassicTheme: PropTypes.bool,
  bannerConfig: PropTypes.object,
  header: PropTypes.object,
  headerTextColor: PropTypes.string,
  boardId: PropTypes.number,
  seller: PropTypes.object,
  isItemView: PropTypes.bool,
  isGreetingOpen: PropTypes.bool,
  contactCard: PropTypes.object,
  lead: PropTypes.object,
  nextItemUrl: PropTypes.string.isRequired,
  prevItemUrl: PropTypes.string.isRequired,
  showLikes: PropTypes.bool
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    setIsGatingFormOpened
  }, dispatch)
}

export default connect(null, mapDispatchToProps)(ItemViewer)
